import { flexRender, type Table } from '@tanstack/react-table';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { Checkbox } from '../Checkbox';
import { Skeleton } from '../Skeleton';
import s from './DataTable.module.css';

const STEP = 30;

/**
 * Card-list mode for narrow containers (IA §4: tables become card lists on phones). The page
 * scrolls, not the list; more cards render as the end comes into view.
 */
export function CardList<T>({
  table,
  caption,
  loading,
  onRowClick,
  activeRowId,
  hasMore,
  onLoadMore,
  primaryId,
  secondaryIds,
}: {
  table: Table<T>;
  caption: string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  activeRowId?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  primaryId?: string;
  secondaryIds: string[];
}) {
  const rows = table.getRowModel().rows;
  const [shown, setShown] = useState(STEP);
  const sentinel = useRef<HTMLDivElement>(null);
  const more = shown < rows.length || !!hasMore;

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !more) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      if (shown < rows.length) setShown((n) => n + STEP);
      else if (hasMore) onLoadMore?.();
    });
    io.observe(node);
    return () => io.disconnect();
  }, [more, shown, rows.length, hasMore, onLoadMore]);

  if (loading) {
    return (
      <ul className={s.cards} aria-busy>
        {Array.from({ length: 3 }, (_, i) => (
          <li key={i} className={s.card}>
            <Skeleton width="60%" height="16px" />
            <Skeleton width="90%" height="12px" />
            <Skeleton width="40%" height="12px" />
          </li>
        ))}
      </ul>
    );
  }

  const onKey = (e: KeyboardEvent<HTMLLIElement>, row: T) => {
    if (e.key === 'Enter' && onRowClick) {
      e.preventDefault();
      onRowClick(row);
    }
  };

  return (
    <>
      <ul className={s.cards} aria-label={caption}>
        {rows.slice(0, shown).map((row) => {
          const cells = row.getVisibleCells().filter((c) => c.column.id !== '_select');
          const title = cells.find((c) => c.column.id === primaryId) ?? cells[0];
          const subtitle = cells.filter((c) => secondaryIds.includes(c.column.id));
          const rest = cells.filter((c) => c !== title && !secondaryIds.includes(c.column.id));
          return (
            <li
              key={row.id}
              className={cx(s.card, onRowClick && s.clickable, row.getIsSelected() && s.selected, row.id === activeRowId && s.active)}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              onKeyDown={onRowClick ? (e) => onKey(e, row.original) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              <div className={s.cardHead}>
                {row.getCanSelect() && <Checkbox checked={row.getIsSelected()} onChange={(v) => row.toggleSelected(v)} aria-label={t('Select row')} />}
                <div className={s.cardTitle}>{title && flexRender(title.column.columnDef.cell, title.getContext())}</div>
              </div>
              {subtitle.length > 0 && (
                <div className={s.cardSub}>
                  {subtitle.map((c) => (
                    <span key={c.id}>{flexRender(c.column.columnDef.cell, c.getContext())}</span>
                  ))}
                </div>
              )}
              {rest.length > 0 && (
                <dl className={s.cardFields}>
                  {rest.map((c) => (
                    <div key={c.id} className={s.cardField}>
                      <dt>{c.column.columnDef.meta?.header}</dt>
                      <dd>{flexRender(c.column.columnDef.cell, c.getContext())}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ul>
      {more && <div ref={sentinel} className={s.sentinel} aria-hidden />}
    </>
  );
}
